import { BookmakerItem } from '@/models/bookmaker';
import { SiteParamItem } from '@/models/siteParam';
import { createBookmakerPageHeaders } from '@/pages/system/bookmaker/BookmakerPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { bookmakerService } from '@/services/bookmakerService';
import ErrorService from '@/services/errorService';
import { siteParamsService } from '@/services/siteParamsService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { IListParams, IPageOptions } from '@/types';
import type { ISiteParamsDetailState } from '@/types/siteParams';
import type { DataTableHeader } from '@/types/vuetify';

const defaultSortBy = [{ key: 'id', order: 'asc' as const }];

function createBookmakerStoreSetup(storeId: string) {
    // --- state ---
    const items = shallowRef<BookmakerItem[]>([]);
    const total = ref<number>(0);
    const loading = ref(false);
    const paramsDetailState = ref<Record<number, ISiteParamsDetailState>>({});
    const loadGeneration = createLoadingGenerationTracker();

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createBookmakerPageHeaders());
    const tableHeaders = computed<DataTableHeader[]>(() => createBookmakerPageHeaders());

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 50,
        rowsPerPageItems: [50, 100, 200],
        sortBy: [...defaultSortBy],
    });

    // --- actions ---
    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const params: IListParams<Record<string, any>> = {
                filters: {},
                options: {
                    sortBy: options.value.sortBy,
                    page: options.value.page,
                    itemsPerPage: options.value.itemsPerPage,
                },
                requiredData: [],
                requiredDictionaries: ['AuditTable'],
            };

            const response = await bookmakerService.list(params);
            items.value = (response.data.rows || []).map((row) => new BookmakerItem(row));
            total.value = response.data.total || 0;
        } catch (error) {
            if (isRequestCanceled(error)) return;
            console.error('Error loading bookmakers:', error);
            items.value = [];
            total.value = 0;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined && value.page !== options.value.page) {
            options.value.page = value.page;
            loadData();
        }
        if (value.itemsPerPage !== undefined && value.itemsPerPage !== options.value.itemsPerPage) {
            options.value.itemsPerPage = value.itemsPerPage;
            options.value.page = 1;
            loadData();
        }
        if (value.sortBy !== undefined) {
            options.value.sortBy = value.sortBy;
            loadData();
        }
    }

    function clearData() {
        items.value = [];
        paramsDetailState.value = {};
        total.value = 0;
        options.value.page = 1;
        loading.value = false;
    }

    function getParamsDetailState(siteId: number): ISiteParamsDetailState {
        paramsDetailState.value[siteId] ??= { loading: false, error: false, rows: [] };
        return paramsDetailState.value[siteId];
    }

    async function loadParams(entityId: number) {
        const state = getParamsDetailState(entityId);
        if (state.loading) return;

        state.loading = true;
        state.error = false;
        state.rows = [];

        try {
            const response = await siteParamsService.listBySiteId(entityId);
            state.rows = response.data.rows.map((row) => new SiteParamItem(row));
        } catch (error) {
            state.error = true;
            ErrorService.log(error);
        } finally {
            state.loading = false;
        }
    }

    async function createParam(entityId: number, param: string, value: string): Promise<boolean> {
        try {
            const item = new SiteParamItem();
            item.siteId = entityId;
            item.param = param;
            item.value = value;

            const response = await siteParamsService.create({
                rows: [item.toCreateApiData()],
            });
            if (response.data.success === false) return false;
            await loadParams(entityId);
            return true;
        } catch {
            return false;
        }
    }

    async function updateParam(entityId: number, param: SiteParamItem, fields: Array<'param' | 'value'>): Promise<boolean> {
        try {
            const response = await siteParamsService.update({
                rows: [param.toUpdateApiData(fields)],
            });
            if (response.data.success === false) return false;
            await loadParams(entityId);
            return true;
        } catch {
            return false;
        }
    }

    async function deleteParam(entityId: number, paramId: number): Promise<boolean> {
        try {
            const response = await siteParamsService.delete({ rows: [{ id: paramId }] });
            if (response.data.success === false) return false;
            await loadParams(entityId);
            return true;
        } catch {
            return false;
        }
    }

    // --- return ---
    return {
        storeId,
        items,
        total,
        loading,
        headers,
        tableHeaders,
        options,
        loadData,
        updateOptions,
        clearData,
        getParamsDetailState,
        loadParams,
        createParam,
        updateParam,
        deleteParam,
        hasRefresh: true,
    };
}

export function useBookmakerStore(storeId: string = 'bookmaker') {
    const useStore = defineStore(storeId, () => createBookmakerStoreSetup(storeId));
    return useStore();
}
